// API contract between the web client (src/web) and the server (src/server).
// Both sides import these types; change them here first.

export type SummaryLang = 'de' | 'en' | 'es' | 'nl';

/**
 * Which summary language to use when none is given explicitly (see
 * src/shared/lang.ts): videos spoken in a `keep` language are summarized in
 * that language, all others in `fallback`.
 */
export interface LangPreference {
  keep: SummaryLang[];
  fallback: SummaryLang;
}

export type TranscriptMode = 'server' | 'client';

export interface TranscriptSegment {
  /** Offset in seconds from the start of the video. */
  start: number;
  text: string;
}

export interface Chapter {
  title: string;
  /** Offset in seconds. */
  start: number;
}

export interface VideoMeta {
  videoId: string;
  title: string;
  channel: string;
  channelUrl?: string;
  durationSeconds?: number;
  /** As delivered by YouTube (ISO date or human-readable text). */
  publishedAt?: string;
  thumbnailUrl?: string;
}

export interface VideoTranscript extends VideoMeta {
  description?: string;
  chapters?: Chapter[];
  /** BCP-47 code of the transcript (caption track) language, e.g. "de" or "en". */
  language?: string;
  /**
   * Base code of the spoken (original) language of the video, e.g. "en", when
   * it can be determined. May differ from `language` when no caption track in
   * the original language exists. Language resolution uses
   * `sourceLanguage ?? language`.
   */
  sourceLanguage?: string;
  isAutoGenerated?: boolean;
  segments: TranscriptSegment[];
}

/**
 * Content of a Codex `auth.json` (ChatGPT login). Opaque to the client: it is
 * stored as-is in localStorage for anonymous users and must be replaced
 * whenever the server returns a refreshed copy.
 */
export type CodexAuthBlob = Record<string, unknown>;

export interface CodexAccount {
  email?: string;
  /** ChatGPT plan, e.g. "plus" or "pro". */
  plan?: string;
}

export interface AppUser {
  id: string;
  email: string;
  name?: string;
}

/** GET /api/me */
export interface MeResponse {
  user: AppUser | null;
  /** Codex login stored on the server (only for signed-in users). */
  codex: { stored: boolean; account?: CodexAccount };
}

/** POST /api/codex/device/start  (no body) */
export interface DeviceStartResponse {
  /** Opaque handle, passed back to /api/codex/device/poll. */
  loginId: string;
  userCode: string;
  verificationUrl: string;
  /** Recommended poll interval in seconds. */
  interval: number;
  expiresAt: string;
}

/** POST /api/codex/device/poll  body: { loginId } */
export type DevicePollResponse =
  | { status: 'pending' }
  | {
      status: 'complete';
      account: CodexAccount;
      /** Only for anonymous users: the client stores it in localStorage. Signed-in users get it stored server-side. */
      auth?: CodexAuthBlob;
    }
  | { status: 'expired' | 'denied' | 'error'; message: string };

/**
 * POST /api/codex/browser/start  (no body)
 * Fallback login without device codes: the user signs in at `authorizeUrl` and
 * ends up on an unreachable http://127.0.0.1:1455/auth/callback?code=… page,
 * whose address they paste back.
 */
export interface BrowserLoginStartResponse {
  loginId: string;
  authorizeUrl: string;
  expiresAt: string;
}
/** POST /api/codex/browser/complete  body: { loginId, redirectUrl }  -> DevicePollResponse (never 'pending') */

/**
 * POST /api/codex/auth  body: { auth: CodexAuthBlob }
 * Validates (and refreshes if needed) a Codex auth.json, e.g. pasted by the user
 * or from localStorage. Signed-in users: stored server-side, `auth` omitted in
 * the response. Anonymous users: the refreshed blob is returned.
 */
export interface CodexAuthResponse {
  account: CodexAccount;
  auth?: CodexAuthBlob;
}
// DELETE /api/codex/auth  -> 204, removes the server-stored login.

/** A model the ChatGPT account may use. */
export interface CodexModelOption {
  slug: string;
  /** Display name, e.g. "GPT-6.1-Sol". */
  name: string;
  /** Reasoning efforts it supports; may be empty (unknown). */
  efforts: string[];
  /** The effort a run uses when none is chosen. */
  defaultEffort?: string;
}

/**
 * POST /api/codex/models  body: { codexAuth? }  (a POST because of the login)
 * The models of the Codex login that summaries of this client run with: the
 * server-stored one, else `codexAuth`. No login: 401 codex_auth_required.
 * `models` is empty when the list could not be loaded right now. `auth`: the
 * browser-stored login was refreshed, the client must replace its copy.
 */
export interface CodexModelsResponse {
  models: CodexModelOption[];
  /** The model a run uses when none is chosen (unset while `models` is empty). */
  defaultModel?: string;
  auth?: CodexAuthBlob;
}

/**
 * GET  /api/transcript?videoId=..                                      -> VideoTranscript | 404
 *   The transcript stored with the video's cached summaries (or by an earlier POST below).
 * POST /api/transcript  body: { videoId, lang? }  (signed-in users only) -> VideoTranscript
 *   Fetched from YouTube by the server (and stored).
 */

export interface SummaryRecord {
  videoId: string;
  lang: SummaryLang;
  title: string;
  channel: string;
  markdown: string;
  model?: string;
  createdAt: string;
  /** True when served from the server cache without running Codex. */
  cached: boolean;
  /** Base code of the video's spoken language ("en", "fr", …), if known. Older summaries lack it. */
  sourceLang?: string;
}

/** Web Push subscription, as PushSubscription.toJSON() returns it. */
export interface PushTarget {
  endpoint: string;
  keys: { p256dh: string; auth: string };
}

/** GET /api/push/key -> PushKeyResponse; 404 when push is not configured on the server. */
export interface PushKeyResponse {
  /** VAPID public key (base64url), the applicationServerKey for pushManager.subscribe(). */
  publicKey: string;
}

/** Payload of the pushes the service worker receives (JSON). */
export interface PushMessage {
  title: string;
  body?: string;
  /** Same-origin path opened on tap. */
  url: string;
  /** Replaces an earlier notification with the same tag (the video id). */
  tag?: string;
}

/**
 * GET /api/summary?videoId=..&lang=..                -> SummaryRecord | 404  (explicit language)
 * GET /api/summary?videoId=..&keep=de,en&fallback=de -> SummaryRecord | 404  (LangPreference;
 *   the server picks the cached summary matching the video's known source
 *   language, see pickForPreference in src/shared/lang.ts)
 */

/**
 * POST /api/summarize -> text/event-stream (see SummarizeEvent).
 *
 * Every summary runs as a server-side job that is independent of the HTTP
 * connection: a client that loses the stream (app minimized on Android,
 * network change, reload) re-attaches with GET /api/summarize/jobs/:jobId and
 * gets every event it missed. Only DELETE cancels a job. The first event is
 * always `job`; every SSE message carries `id: <index>` (0-based position of
 * the event within the job, the `job` event is 0). A POST that matches a job
 * of the same client that is still running (same videoId, lang/langPref,
 * force=false, same user — or, anonymous, the same Codex login) joins that job
 * (its events are replayed from the start) instead of starting a second run.
 *
 * - With `transcript`: client-side transcript mode.
 * - Without `transcript`: server fetches it (signed-in users only).
 * - `codexAuth`: required for anonymous users (browser-stored login); signed-in
 *   users without a server-stored login may send it too.
 * - `force`: ignore the cache and regenerate.
 * - `model`/`effort`: the user's choice for a newly generated summary. Omitted,
 *   or not available to the account: the default (see POST /api/codex/models).
 *   Cached summaries are served whatever model wrote them.
 */
export interface SummarizeRequest {
  videoId: string;
  /** Explicit summary language. When omitted, `langPref` is required and the server resolves the language from the transcript. */
  lang?: SummaryLang;
  langPref?: LangPreference;
  transcript?: VideoTranscript;
  codexAuth?: CodexAuthBlob;
  force?: boolean;
  /** Model slug (CodexModelOption.slug). */
  model?: string;
  /** Reasoning effort (CodexModelOption.efforts). */
  effort?: string;
  /**
   * Web Push subscription of this browser (PushSubscription.toJSON()). When
   * set, the server pushes "summary ready" when the summary starts being
   * written (`status: writing`), or a failure when the job ends with an error
   * before that. The service worker drops it while the app is in front.
   */
  push?: PushTarget;
}

/**
 * - summarizing: the model reads the transcript and reasons (`progress` notes).
 * - writing: the model writes the summary; `delta` events follow as it is
 *   generated. Reached once per run, when the first words exist: from here on
 *   the summary can be read (the "ready" notification is sent at this point).
 */
export type SummarizeStage = 'cache' | 'transcript' | 'summarizing' | 'writing';

export type ErrorCode =
  | 'bad_request'
  | 'login_required'
  | 'codex_auth_required'
  | 'codex_auth_invalid'
  | 'codex_quota'
  | 'video_not_found'
  | 'transcript_unavailable'
  /**
   * YouTube refused the server's request (rate limit, bot check). The client
   * retries automatically with the transcript fetched in the browser.
   */
  | 'youtube_blocked'
  /** Too many requests from this IP/user; retry later (HTTP 429, Retry-After). */
  | 'rate_limited'
  /** GET/DELETE /api/summarize/jobs/:jobId: the job is unknown or expired (e.g. after a server restart). */
  | 'job_not_found'
  | 'internal';

/** One SSE message: `event: <type>` + `data: <JSON of the rest>`. */
/**
 * GET /api/summarize/jobs/:jobId?after=<index>  -> text/event-stream
 *   Replays the job's events with an index > `after` (all when omitted), then
 *   follows it live until `done`/`error`. Unknown or expired job: HTTP 404
 *   `job_not_found` (the client then checks the cache and starts anew).
 *   Jobs of signed-in users are only visible to that user. Without `after`,
 *   a numeric Last-Event-ID header is used the same way.
 *   Ids only increase but may have gaps: a replay keeps only the latest
 *   `progress` event (superseded ones are skipped). A stream may also end
 *   without `done`/`error` (server shutdown): re-attach, then 404 follows.
 * DELETE /api/summarize/jobs/:jobId  -> 204 (idempotent; 404 when unknown is fine too)
 *   Cancels the job (the user pressed "Abbrechen" or closed the tab). Streams
 *   still attached to it end with an `error` event (code `internal`).
 */
export const SUMMARY_JOB_RETENTION_MS = 30 * 60_000;
/** Max. run time of a summary job; the server aborts it afterwards. */
export const SUMMARY_JOB_MAX_RUN_MS = 20 * 60_000;

export type SummarizeEvent =
  /** First event of every stream: the handle to re-attach with. */
  | { type: 'job'; jobId: string }
  | { type: 'status'; stage: SummarizeStage; message?: string }
  /**
   * The effective summary language, as soon as it is known (before 'meta' and
   * the first 'delta'). Always sent, also for an explicit `lang`.
   */
  | { type: 'lang'; lang: SummaryLang; sourceLang?: string }
  | { type: 'meta'; meta: VideoMeta }
  /**
   * Markdown appended to the summary so far: first the heading (title +
   * creator, right after `meta`), then the model's answer while it is
   * generated (after `status: writing`). `done` carries the complete text.
   */
  | { type: 'delta'; text: string }
  /** Short progress note from the model, e.g. a reasoning summary. */
  | { type: 'progress'; text: string }
  /**
   * Refreshed browser-stored Codex login: the client must replace its copy.
   * Replayed events carry the newest known version of the login (never an
   * already rotated one).
   */
  | { type: 'auth'; auth: CodexAuthBlob }
  | { type: 'done'; summary: SummaryRecord }
  | { type: 'error'; code: ErrorCode; message: string };

/**
 * An open tab: a summary the user opened (shared from the YouTube app, pasted,
 * picked from the history) and has not closed yet. One tab per video.
 * Signed-in users: stored on the server (synced across devices). Anonymous
 * users: localStorage only.
 */
export interface OpenTab {
  videoId: string;
  /** Language of the shown summary; omitted while it is still being resolved from the preference. */
  lang?: SummaryLang;
  /** May be empty while the video's metadata is still loading. */
  title: string;
  channel: string;
  /** ISO time the tab was first opened; tabs are listed oldest first. */
  openedAt: string;
}

/** Max. open tabs per user; when exceeded, the oldest tabs are dropped. */
export const MAX_OPEN_TABS = 50;

/**
 * Open tabs of the signed-in user (all 401 login_required otherwise). Every
 * call answers with the full, current list (oldest first):
 * GET    /api/tabs                                         -> TabsResponse
 * PUT    /api/tabs/:videoId   body: { lang?, title, channel } -> TabsResponse
 *        (insert, or update lang/title/channel of an existing tab and keep its openedAt;
 *         empty title/channel never overwrite known ones)
 * DELETE /api/tabs/:videoId                                -> TabsResponse (idempotent)
 * POST   /api/tabs/import     body: { tabs: OpenTab[] }   -> TabsResponse
 *        (merges browser-stored tabs after sign-in; existing server tabs win)
 */
export interface TabsResponse {
  tabs: OpenTab[];
}

/** Error body of non-SSE endpoints (HTTP 4xx/5xx). */
export interface ApiError {
  error: { code: ErrorCode; message: string };
}
